import { Stack, Grid, Typography, InputAdornment, Box } from "@mui/material";
import { FormField } from "@/components/elements/FormField/FormField";
import { Input } from "@/components/elements/Input/Input";
import { InputDatePicker } from "@/components/elements/InputDatePicker/InputDatePicker";
import { Select } from "@/components/elements/Select/Select";
import { SelectMulti } from "@/components/elements/SelectMulti";
import { Textarea } from "@/components/elements/Textarea/Textarea";
import { InnerBox } from "@/components/elements/InnerBox";
import { useGetUserListPersonInCharge } from "@/api/services/main/user";
import {
  useGetLanguageTestList,
  useGetAttachmentDocumentTypeList,
  useGetMajorList,
  useGetSpecificSkillCertificateList,
} from "@/api/services/global/master";
import { black, orange, text } from "@/theme/colors";
import { findDuplicateResidenceCardTypeIds, DUPLICATE_RESIDENCE_CARD_TYPE_MESSAGE } from "@/utils/attachmentDocumentType";
import { DocumentTypeTagsRow, MAX_DOCUMENT_TYPES } from "@/features/talent/components/talent-form/DocumentTypeTagsRow";
import { useForm, Resolver } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import dayjs, { isDayjs } from "dayjs";
import { useCaseMatchInfoSchema } from "@/features/case/schemas/caseMatchInfoSchema";
import { isDateSameOrAfter } from "@/features/case/utils/caseMatchHelper";
import { Form } from "@/components/elements/Form";
import { Button } from "@/components/elements/Button";
import { CheckboxGroup } from "@/components/elements/CheckboxGroup";
import { useTagOptions } from "@/api/services/main/tag";
import { CaseMatchTagEditorModal } from "@/features/case/components/case-match-tag-editor/CaseMatchTagEditorModal";
import { TCaseMatches } from "@/api/services/main/case/dtos/get-case.dto";
import { TCaseMatchInfoForm } from "@/features/case/types/caseMatchInfo";
import { forwardRef, Key, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useWatch } from "react-hook-form";
import { CASE_MATCH_DOCUMENT_TYPE_IDS, ECaseMatchStatus } from "@/types/enum";
import { TTalentForm } from "@/features/talent/types/talentForm";
import { TalentDropZone } from "@/features/talent/components/talent-form/TalentDropZone";
import { MAX_SIZE, TALENT_FILE_TYPES_EXTENSION, TALENT_MINE_TYPES } from "@/features/talent/constants/file";
import { TExtendFile } from "@/types/file";
import { FileRejection } from "react-dropzone/.";
import { getExtensionsOfMimeTypes, TFileRules } from "@/utils/file";
import { toast } from "@/utils/toast";
import { updateCaseMatchApi } from "@/api/services/main/case";
import { updateTalentFromCaseMatchApi } from "@/api/services/main/talent";
import { TUpdateTalentFromCaseMatchReqDto } from "@/api/services/main/talent/dtos/update-talent-from-case-match.dto";
import { useParams } from "react-router-dom";
import { API_SUCCESS_MESSAGE, CASE_MATCH_INFO_NOT_FOUND_MESSAGE } from "@/constants/common";
import { showError } from "@/utils/error";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { normalizeString } from "@/utils/string";
import { CERTIFICATES_REQUIRING_MAJOR_JOB_TYPE } from "@/features/case/utils/checkMissingRequiredFields";
import { DATE_FORMAT_FOR_API } from "@/constants/datetime";
import { scrollToFirstError } from "@/utils/scrollToFirstError";

const MAX_TOTAL_FILES = 10;

type TCaseMatchInfoFormProps = {
  caseMatch?: TCaseMatches;
  talent?: Partial<TTalentForm>;
  currentStatus?: number;
  onSuccess?: () => void;
  onLoadingChange?: (isLoading: boolean) => void;
  canEdit?: boolean;
  exemptSpecifiedSkillCert?: boolean;
};

export const CaseMatchInfoForm = forwardRef<{ submit: () => void; isLoading: boolean }, TCaseMatchInfoFormProps>(
  ({ caseMatch, talent, currentStatus, onSuccess, onLoadingChange, canEdit = true, exemptSpecifiedSkillCert = false }, ref) => {
    const { caseId } = useParams();
    const queryClient = useQueryClient();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const prevCasePersonInChange = caseMatch?.caseMatchUserId ?? null;
    const prevTalentPersonInChange = caseMatch?.talentMatchUserId ?? null;
    const [caseValidPersonInChange, setCaseValidPersonInChange] = useState<number | null | undefined>();
    const [talentValidPersonInChange, setTalentValidPersonInChange] = useState<number | null | undefined>();
    const { userOptions: caseUserOption } = useGetUserListPersonInCharge(caseValidPersonInChange);
    const { userOptions: talentUserOption } = useGetUserListPersonInCharge(talentValidPersonInChange);
    const { languageTestLevelOptions, data: languageTestData } = useGetLanguageTestList();
    const { majorOptions, getJobTypeListOptionByMajor, isLoading: isLoadingMajor } = useGetMajorList();
    const { attachmentDocumentTypeOptions } = useGetAttachmentDocumentTypeList();
    const { data: specificSkillCertificateData, getCertificatesByIndustryId } = useGetSpecificSkillCertificateList();
    const status = currentStatus ?? caseMatch?.status;
    const talentLocation = talent?.location;

    // Check if talent has selected any of the specific certificates that require majorId/jobTypeId
    const initialRequiresMajorAndJobType = useMemo(() => {
      if (!talent?.specificSkillCertificates || !specificSkillCertificateData) return false;

      const selectedCertIds = talent.specificSkillCertificates.filter((id): id is number => id !== null && id !== undefined);
      if (selectedCertIds.length === 0) return false;

      return selectedCertIds.some((certId) => {
        const certificate = specificSkillCertificateData.find((cert) => cert.id === certId);
        return certificate && CERTIFICATES_REQUIRING_MAJOR_JOB_TYPE.includes(certificate.certificateName);
      });
    }, [talent?.specificSkillCertificates, specificSkillCertificateData]);

    // State to track requiresMajorAndJobType based on form value (will be updated after form is created)
    const [requiresMajorAndJobType, setRequiresMajorAndJobType] = useState(initialRequiresMajorAndJobType);

    const { caseMatchInfoSchema } = useCaseMatchInfoSchema({
      status,
      requiresMajorAndJobType,
      talentLocation,
      exemptSpecifiedSkillCert,
    });

    const defaultValues = useMemo(() => {
      let japaneseLanguageProficiency: (string | number | null)[] | null = null;
      if (talent?.languageCertificates && talent.languageCertificates.length > 0) {
        const languageTestLevelIds: (string | number)[] = [];
        talent.languageCertificates.forEach((cert) => {
          if (cert.languageTestLevelId) {
            const value = Number(cert.languageTestLevelId);
            languageTestLevelIds.push(Number(value));
          } else if (cert.languageTestId) {
            const testId = Number(cert.languageTestId);
            if (testId === 1) {
              languageTestLevelIds.push(-1);
            } else {
              languageTestLevelIds.push(Number(testId));
            }
          }
        });
        japaneseLanguageProficiency = languageTestLevelIds.length > 0 ? languageTestLevelIds : null;
      }

      const majorId = talent?.majorId ? Number(talent.majorId) : null;
      const jobTypeId = talent?.jobTypeId ? Number(talent.jobTypeId) : null;
      const specificSkillCertificates = talent?.specificSkillCertificates?.filter((id): id is number => id !== null && id !== undefined) ?? null;

      // Load files whose attachmentDocumentTypeIds intersect CASE_MATCH_DOCUMENT_TYPE_IDS; keep their FULL tag list
      // (including non-case-match tags) so users can edit them inline without losing context.
      let certificateFiles: TExtendFile[] | null = null;
      let certificateFileTypes: string[] | null = null;
      if (talent?.talentUploadFiles && talent.talentUploadFiles.length > 0) {
        const relevant = talent.talentUploadFiles.filter((f) => f.attachmentDocumentTypeIds?.some((id) => CASE_MATCH_DOCUMENT_TYPE_IDS.includes(id)));
        if (relevant.length > 0) {
          certificateFiles = relevant.map(
            (f) =>
              ({
                id: String(f.id),
                name: f.filePath.split("/").pop() || "",
                filePath: f.filePath,
                type: f.filePath.split(".").pop() || "",
                updatedAt: f.updatedAt || new Date().toISOString(),
              }) as unknown as TExtendFile,
          );
          certificateFileTypes = relevant.map((f) => f.attachmentDocumentTypeIds?.map(String).join(",") || "");
        }
      }

      return {
        casePersonInChargeId: caseMatch?.caseMatchUserId ?? caseMatch?.case?.personInChargeId ?? null,
        talentPersonInChargeId: talent?.personInChargeId ?? null,
        proposalDate: caseMatch?.proposalDate ? dayjs(caseMatch.proposalDate) : null,
        interviewDate: caseMatch?.interviewDate ? dayjs(caseMatch.interviewDate) : null,
        japaneseLanguageProficiency,
        specificSkillCertificates,
        majorId,
        jobTypeId,
        certificateFiles,
        certificateFileTypes,
        fitNote: caseMatch?.fitNote ?? null,
        companyOfferDate: caseMatch?.companyOfferDate ? dayjs(caseMatch.companyOfferDate) : null,
        offerAcceptDate: caseMatch?.offerAcceptDate ? dayjs(caseMatch.offerAcceptDate) : null,
        joinDateExpected: caseMatch?.joinDateExpected ? dayjs(caseMatch.joinDateExpected) : null,
        salesFee: caseMatch?.salesFee ?? null,
        purchaseFee: caseMatch?.purchaseFee ?? null,
        companyJoiningDate: caseMatch?.companyJoiningDate ? dayjs(caseMatch.companyJoiningDate) : null,
        tagIds: caseMatch?.tagIds ? caseMatch.tagIds.map(String) : null,
      };
    }, [caseMatch, talent]);

    // Recreate resolver when schema changes
    const resolver = useMemo(() => yupResolver(caseMatchInfoSchema) as unknown as Resolver<TCaseMatchInfoForm>, [caseMatchInfoSchema]);

    const methods = useForm<TCaseMatchInfoForm>({
      defaultValues,
      resolver,
      mode: "all",
      reValidateMode: "onBlur",
    });

    const { control, handleSubmit, reset, setValue, getValues, trigger } = methods;

    useEffect(() => {
      if (status !== undefined) {
        trigger();
      }
    }, [status, trigger, resolver]);

    const casePersonInChargeId = useWatch({ control, name: "casePersonInChargeId" });
    const talentPersonInChargeId = useWatch({ control, name: "talentPersonInChargeId" });
    const certificateFiles = useWatch({ control, name: "certificateFiles" });
    const certificateFileTypes = useWatch({ control, name: "certificateFileTypes" });
    const specificSkillCertificatesValue = useWatch({ control, name: "specificSkillCertificates" });
    const majorIdValue = useWatch({ control, name: "majorId" });
    const jobTypeIdValue = useWatch({ control, name: "jobTypeId" }) as number | null | undefined;
    const proposalDateValue = useWatch({ control, name: "proposalDate" });
    const interviewDateValue = useWatch({ control, name: "interviewDate" });
    const tagIdsValue = useWatch({ control, name: "tagIds" });

    const { data: caseMatchTagOptions } = useTagOptions("case-match");
    const [isTagEditorOpen, setIsTagEditorOpen] = useState(false);
    const handleTagIdsChange = (value: Key[]) => {
      setValue("tagIds", value.length > 0 ? value.map(String) : null, { shouldDirty: true });
    };
    const companyOfferDateValue = useWatch({ control, name: "companyOfferDate" });
    const offerAcceptDateValue = useWatch({ control, name: "offerAcceptDate" });
    const joinDateExpectedValue = useWatch({ control, name: "joinDateExpected" });
    const companyJoiningDateValue = useWatch({ control, name: "companyJoiningDate" });
    const prevMajorIdRef = useRef<number | null | undefined>(undefined);

    useEffect(() => {
      const hasInCaseUserList = caseUserOption.find((user) => user.value === prevCasePersonInChange);
      if (!hasInCaseUserList) {
      }
      setCaseValidPersonInChange(prevCasePersonInChange);
      setTalentValidPersonInChange(prevTalentPersonInChange);
    }, [prevCasePersonInChange, prevTalentPersonInChange]);

    // Cross-field date validation triggers
    // 推薦日 ≦ 面接日 ≦ 企業内定日 ≦ 内定受諾日 ≦ 企業入社日（予定）/ 企業入社日
    useEffect(() => {
      if (isDayjs(proposalDateValue) && isDayjs(interviewDateValue) && isDateSameOrAfter(interviewDateValue, proposalDateValue)) {
        trigger(["proposalDate", "interviewDate"]);
      }
    }, [proposalDateValue, interviewDateValue, trigger]);

    useEffect(() => {
      if (isDayjs(interviewDateValue) && isDayjs(companyOfferDateValue) && isDateSameOrAfter(companyOfferDateValue, interviewDateValue)) {
        trigger(["interviewDate", "companyOfferDate"]);
      }
    }, [interviewDateValue, companyOfferDateValue, trigger]);

    useEffect(() => {
      if (isDayjs(companyOfferDateValue) && isDayjs(offerAcceptDateValue) && isDateSameOrAfter(offerAcceptDateValue, companyOfferDateValue)) {
        trigger(["companyOfferDate", "offerAcceptDate"]);
      }
    }, [companyOfferDateValue, offerAcceptDateValue, trigger]);

    useEffect(() => {
      if (isDayjs(offerAcceptDateValue) && isDayjs(joinDateExpectedValue) && isDateSameOrAfter(joinDateExpectedValue, offerAcceptDateValue)) {
        trigger(["offerAcceptDate", "joinDateExpected"]);
      }
    }, [offerAcceptDateValue, joinDateExpectedValue, trigger]);

    useEffect(() => {
      if (isDayjs(offerAcceptDateValue) && isDayjs(companyJoiningDateValue) && isDateSameOrAfter(companyJoiningDateValue, offerAcceptDateValue)) {
        trigger(["offerAcceptDate", "companyJoiningDate"]);
      }
    }, [offerAcceptDateValue, companyJoiningDateValue, trigger]);

    // Update requiresMajorAndJobType when form value changes
    useEffect(() => {
      if (!specificSkillCertificateData) return;

      const selectedCertIds = (specificSkillCertificatesValue ?? []).filter((id): id is number => id !== null && id !== undefined);

      const shouldRequire = selectedCertIds.some((certId) => {
        const certificate = specificSkillCertificateData.find((cert) => cert.id === certId);
        return certificate && CERTIFICATES_REQUIRING_MAJOR_JOB_TYPE.includes(certificate.certificateName);
      });

      setRequiresMajorAndJobType(shouldRequire);
    }, [specificSkillCertificatesValue, specificSkillCertificateData]);

    useEffect(() => {
      trigger(["majorId", "jobTypeId"]);
    }, [requiresMajorAndJobType, trigger]);

    // Clear jobTypeId when majorId changes
    useEffect(() => {
      if (prevMajorIdRef.current === undefined) {
        prevMajorIdRef.current = majorIdValue;
        if (!majorIdValue) {
          setValue("jobTypeId", null, { shouldDirty: false });
        }
        return;
      }

      if (prevMajorIdRef.current !== majorIdValue) {
        prevMajorIdRef.current = majorIdValue;

        if (!majorIdValue) {
          setValue("jobTypeId", null, { shouldDirty: false });
          return;
        }

        // Check if current value is still valid for new major
        const validOptions = getJobTypeListOptionByMajor(Number(majorIdValue));
        const validOptionValues = new Set(validOptions.map((opt) => opt.value));

        if (jobTypeIdValue !== null && jobTypeIdValue !== undefined) {
          if (!validOptionValues.has(jobTypeIdValue)) {
            setValue("jobTypeId", null, { shouldDirty: false });
          }
        }
      }
    }, [majorIdValue, jobTypeIdValue, getJobTypeListOptionByMajor, setValue]);

    const TERMINAL_STATUSES = [
      ECaseMatchStatus.FAILED,
      ECaseMatchStatus.CANDIDATE_WITHDRAWN,
      ECaseMatchStatus.OFFER_CANCELLED,
      ECaseMatchStatus.SELECTION_CANCELLED,
    ];

    const isTerminalStatus = status !== undefined && status !== null && TERMINAL_STATUSES.includes(status as ECaseMatchStatus);

    const isStatusAtLeast = (minStatus: ECaseMatchStatus) => {
      if (status === undefined || status === null) return false;
      if (isTerminalStatus) return false;
      return status >= minStatus;
    };

    const requiresProposalDate = isStatusAtLeast(ECaseMatchStatus.PROPOSING);
    const requiresInterviewDate = isStatusAtLeast(ECaseMatchStatus.UNDER_SELECTION);
    const requiresJapaneseLanguageProficiency = isStatusAtLeast(ECaseMatchStatus.PROPOSING);
    const requiresSpecifiedSkillCertificateFile = isStatusAtLeast(ECaseMatchStatus.PROPOSING) && !exemptSpecifiedSkillCert;
    const requiresCertificateFiles = isStatusAtLeast(ECaseMatchStatus.PROPOSING);
    const requiresMajorId = isStatusAtLeast(ECaseMatchStatus.PROPOSING) && requiresMajorAndJobType;
    const requiresJobTypeId = isStatusAtLeast(ECaseMatchStatus.PROPOSING) && requiresMajorAndJobType;
    const requiresCompanyOfferDate = isStatusAtLeast(ECaseMatchStatus.OFFER);
    const requiresOfferAcceptDate = isStatusAtLeast(ECaseMatchStatus.OFFER_ACCEPTED);
    const requiresJoinDateExpected = isStatusAtLeast(ECaseMatchStatus.OFFER_ACCEPTED);
    const requiresSalesFee = isStatusAtLeast(ECaseMatchStatus.OFFER_ACCEPTED);
    const requiresPurchaseFee = isStatusAtLeast(ECaseMatchStatus.OFFER_ACCEPTED);
    const requiresCompanyJoiningDate = !isTerminalStatus && status === ECaseMatchStatus.ALREADY_HIRED;

    const rules: TFileRules = { acceptMimeTypes: TALENT_MINE_TYPES, acceptFileTypesExtension: TALENT_FILE_TYPES_EXTENSION };

    // Count of files outside CASE_MATCH_DOCUMENT_TYPE_IDS (still occupy MAX_TOTAL_FILES quota even though not editable here).
    const otherFilesCount = useMemo(() => {
      if (!talent?.talentUploadFiles) return 0;
      return talent.talentUploadFiles.filter((file) => {
        return !file.attachmentDocumentTypeIds?.some((id) => CASE_MATCH_DOCUMENT_TYPE_IDS.includes(id));
      }).length;
    }, [talent?.talentUploadFiles]);

    const getRemainingAllowedFiles = (): number => {
      return MAX_TOTAL_FILES - otherFilesCount - (certificateFiles?.length ?? 0);
    };

    useEffect(() => {
      // keepDirtyValues preserves user-modified fields if defaultValues recomputes mid-edit
      // (e.g., React Query refetch updates `talent` prop). Without it, in-flight edits would be wiped
      // and the unsaved-changes confirm modal would no longer trigger on close.
      reset(defaultValues, { keepDirtyValues: true });
    }, [defaultValues, reset]);

    const onSubmit = async (data: TCaseMatchInfoForm) => {
      if (!caseMatch?.id || !caseId) {
        toast.error(CASE_MATCH_INFO_NOT_FOUND_MESSAGE);
        return;
      }

      setIsSubmitting(true);
      onLoadingChange?.(true);
      try {
        const caseMatchUpdatePayload: {
          caseMatchUserId?: number | null;
          talentMatchUserId?: number | null;
          proposalDate?: string | null;
          interviewDate?: string | null;
          identificationFile?: string | null;
          companyOfferDate?: string | null;
          fitNote?: string | null;
          offerAcceptDate?: string | null;
          joinDateExpected?: string | null;
          salesFee?: number | null;
          purchaseFee?: number | null;
          companyJoiningDate?: string | null;
          tagIds?: string[] | null;
        } = {
          caseMatchUserId: data.casePersonInChargeId,
          talentMatchUserId: data.talentPersonInChargeId,
          proposalDate: data.proposalDate ? data.proposalDate.format(DATE_FORMAT_FOR_API) : null,
          interviewDate: data.interviewDate ? data.interviewDate.format(DATE_FORMAT_FOR_API) : null,
          companyOfferDate: data.companyOfferDate ? data.companyOfferDate.format(DATE_FORMAT_FOR_API) : null,
          fitNote: normalizeString(data.fitNote),
          offerAcceptDate: data.offerAcceptDate ? data.offerAcceptDate.format(DATE_FORMAT_FOR_API) : null,
          joinDateExpected: data.joinDateExpected ? data.joinDateExpected.format(DATE_FORMAT_FOR_API) : null,
          salesFee: data.salesFee ?? null,
          purchaseFee: data.purchaseFee ?? null,
          companyJoiningDate: data.companyJoiningDate ? data.companyJoiningDate.format(DATE_FORMAT_FOR_API) : null,
          tagIds: data.tagIds ?? null,
        };

        await updateCaseMatchApi(Number(caseId), caseMatch.id, caseMatchUpdatePayload);

        if (talent?.id) {
          const existingCaseMatchFiles =
            talent.talentUploadFiles?.filter((f) => f.attachmentDocumentTypeIds?.some((id) => CASE_MATCH_DOCUMENT_TYPE_IDS.includes(id))) || [];

          const formFiles = data.certificateFiles || [];
          const formFileTypes = data.certificateFileTypes || [];

          // Split form files into uploads vs kept (existing); preserve type CSVs in parallel arrays.
          const filesToUpload: File[] = [];
          const newUploadFileTypes: string[] = [];
          const keptFilePaths: string[] = [];
          const keptFileTypes: string[] = [];
          formFiles.forEach((file, index) => {
            const fileTypeCsv = formFileTypes[index] ?? "";
            if (file instanceof File) {
              filesToUpload.push(file);
              newUploadFileTypes.push(fileTypeCsv);
            } else {
              const extendFile = file as TExtendFile;
              if (extendFile.filePath) {
                keptFilePaths.push(extendFile.filePath);
                keptFileTypes.push(fileTypeCsv);
              }
            }
          });

          let hasTalentChanges = false;

          const newPersonInChargeId = data.talentPersonInChargeId ?? talent.personInChargeId;
          if (newPersonInChargeId !== talent.personInChargeId) {
            hasTalentChanges = true;
          }

          const newMajorId = data.majorId !== undefined ? data.majorId : talent.majorId;
          if (newMajorId !== talent.majorId) {
            hasTalentChanges = true;
          }

          const newJobTypeId = data.jobTypeId !== undefined ? data.jobTypeId : talent.jobTypeId;
          if (newJobTypeId !== talent.jobTypeId) {
            hasTalentChanges = true;
          }

          // Check if specificSkillCertificates changed
          const currentSpecificSkillCertificates =
            talent.specificSkillCertificates?.filter((id): id is number => id !== null && id !== undefined) || [];
          const newSpecificSkillCertificates = data.specificSkillCertificates?.filter((id): id is number => id !== null && id !== undefined) || [];
          const specificSkillCertificatesChanged =
            currentSpecificSkillCertificates.length !== newSpecificSkillCertificates.length ||
            currentSpecificSkillCertificates.some((id) => !newSpecificSkillCertificates.includes(id)) ||
            newSpecificSkillCertificates.some((id) => !currentSpecificSkillCertificates.includes(id));
          if (specificSkillCertificatesChanged) {
            hasTalentChanges = true;
          }

          if (filesToUpload.length > 0) {
            hasTalentChanges = true;
          }

          const existingFileTypesMap = new Map<string, string>();
          existingCaseMatchFiles.forEach((f) => {
            existingFileTypesMap.set(f.filePath, f.attachmentDocumentTypeIds?.map(String).join(",") || "");
          });
          const existingPaths = existingCaseMatchFiles.map((f) => f.filePath);
          const filesRemoved = existingPaths.some((path) => !keptFilePaths.includes(path));
          // Detect retag: same path kept but its CSV type list differs from what's on the server.
          const typesChanged = keptFilePaths.some((path, i) => (existingFileTypesMap.get(path) || "") !== (keptFileTypes[i] || ""));
          if (filesRemoved || typesChanged) {
            hasTalentChanges = true;
          }

          const currentLanguageTestLevelIds = talent.languageTestLevelIds || [];
          const newLanguageTestLevelIds =
            data.japaneseLanguageProficiency !== null &&
            data.japaneseLanguageProficiency !== undefined &&
            Array.isArray(data.japaneseLanguageProficiency)
              ? data.japaneseLanguageProficiency
                  .filter((id) => id !== null && id !== undefined)
                  .map((id) => (typeof id === "string" ? Number(id) : id))
                  .filter((id): id is number => typeof id === "number")
              : [];

          const languageIdsChanged =
            currentLanguageTestLevelIds.length !== newLanguageTestLevelIds.length ||
            currentLanguageTestLevelIds.some((id, index) => id !== newLanguageTestLevelIds[index]);
          if (languageIdsChanged) {
            hasTalentChanges = true;
          }

          if (hasTalentChanges) {
            const minimalPayload: TUpdateTalentFromCaseMatchReqDto = {};

            if (newPersonInChargeId !== talent.personInChargeId) {
              minimalPayload.personInChargeId = newPersonInChargeId ?? 1;
            }

            if (newMajorId !== talent.majorId) {
              minimalPayload.majorId = newMajorId ?? null;
            }

            if (newJobTypeId !== talent.jobTypeId) {
              minimalPayload.jobTypeId = newJobTypeId ?? null;
            }

            if (specificSkillCertificatesChanged) {
              minimalPayload.specificSkillCertificates = newSpecificSkillCertificates.length > 0 ? newSpecificSkillCertificates : null;
            }

            if (languageIdsChanged) {
              if (newLanguageTestLevelIds.length > 0) {
                const languageCertificates: Array<{ languageTestId: number; languageTestLevelId?: number | null }> = [];
                newLanguageTestLevelIds.forEach((id) => {
                  if (id === null || id === undefined) return;
                  if (id < 0) {
                    const testId = -id;
                    languageCertificates.push({
                      languageTestId: testId,
                      languageTestLevelId: null,
                    });
                  } else {
                    const test = languageTestData?.find((t) => t.levels.some((level) => level.id.toString() === id.toString()));
                    if (test) {
                      languageCertificates.push({
                        languageTestId: test.id,
                        languageTestLevelId: id,
                      });
                    }
                  }
                });
                minimalPayload.languageCertificates = languageCertificates.length > 0 ? languageCertificates : null;
              } else {
                minimalPayload.languageCertificates = null;
              }
            }

            if (filesToUpload.length > 0 || filesRemoved || typesChanged) {
              // Preserve files not managed by case match (their types stay untouched).
              const otherFiles =
                talent.talentUploadFiles?.filter((file) => {
                  return !file.attachmentDocumentTypeIds?.some((id) => CASE_MATCH_DOCUMENT_TYPE_IDS.includes(id));
                }) || [];
              const otherFilePaths = otherFiles.map((f) => f.filePath);
              const otherFileTypes = otherFiles.map((f) => f.attachmentDocumentTypeIds?.map(String).join(",") || "");

              // Payload order must align: relatedFilePaths = others + kept; relatedFileTypes = others + kept + new uploads (in upload order).
              const allFilePaths = [...otherFilePaths, ...keptFilePaths];
              const allFileTypes = [...otherFileTypes, ...keptFileTypes, ...newUploadFileTypes];

              if (filesToUpload.length > 0) {
                minimalPayload.relatedFiles = filesToUpload;
              }

              minimalPayload.relatedFileTypes = JSON.stringify(allFileTypes);
              minimalPayload.relatedFilePaths = JSON.stringify(allFilePaths);
            }

            if (Object.keys(minimalPayload).length > 0) {
              await updateTalentFromCaseMatchApi(talent.id, minimalPayload);
            }
          }
        }

        toast.success(API_SUCCESS_MESSAGE);

        onSuccess?.();

        await new Promise((resolve) => setTimeout(resolve, 150));

        await queryClient.refetchQueries({ queryKey: [QUERY_KEYS.CASE.FETCH_CASE_MATCHES, Number(caseId)] });

        if (talent?.id) {
          await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TALENT.FETCH_DETAIL, talent.id] });
        }
      } catch (e) {
        showError(e);
      } finally {
        setIsSubmitting(false);
        onLoadingChange?.(false);
      }
    };

    // Schema's "required-types" test lives on certificateFileTypes and redirects error to certificateFiles path.
    // After any file/type mutation, trigger BOTH fields so the test runs and the error attaches/clears correctly.
    const revalidateCertificate = () => trigger(["certificateFileTypes", "certificateFiles"]);

    const onCertificateFilesChange = (value: TExtendFile | TExtendFile[] | null) => {
      if (!value) {
        setValue("certificateFiles", null, { shouldDirty: true });
        setValue("certificateFileTypes", null, { shouldDirty: true });
        revalidateCertificate();
        return;
      }

      const newFiles = (Array.isArray(value) ? value : [value]).filter(Boolean) as TExtendFile[];
      if (newFiles.length === 0) {
        setValue("certificateFiles", null, { shouldDirty: true });
        setValue("certificateFileTypes", null, { shouldDirty: true });
        revalidateCertificate();
        return;
      }

      // Map existing file → its CSV type so newly added files don't lose tags when the array re-renders.
      const prevTypes = getValues("certificateFileTypes") || [];
      const prevFiles = getValues("certificateFiles") || [];
      const idToType = new Map<string, string>();
      const pathToType = new Map<string, string>();
      prevFiles.forEach((file, i) => {
        const type = prevTypes[i] ?? "";
        if (file.id) idToType.set(String(file.id), type);
        if (file.filePath) pathToType.set(file.filePath, type);
      });

      const nextTypes = newFiles.map((file) => {
        if (file.id && idToType.has(String(file.id))) return idToType.get(String(file.id)) || "";
        if (file.filePath && pathToType.has(file.filePath)) return pathToType.get(file.filePath) || "";
        return "";
      });

      setValue("certificateFileTypes", nextTypes, { shouldDirty: true });
      setValue("certificateFiles", newFiles, { shouldDirty: true });
      revalidateCertificate();
    };

    // Use per-index path syntax (certificateFileTypes.${fileIndex}) so react-hook-form treats each
    // file's tag list as a field-array element. Whole-array setValue can fail to register dirty state
    // reliably for array fields; per-index path mirrors the working pattern in TalentFormCertificateFiles.
    const handleTypeChange = (fileIndex: number, typeIndex: number, newValue: string) => {
      const prevTypes = getValues("certificateFileTypes") || [];
      const csv = prevTypes[fileIndex] || "";
      const ids = csv ? csv.split(",").filter(Boolean) : [];
      if (newValue) {
        ids[typeIndex] = newValue;
      } else {
        ids.splice(typeIndex, 1);
      }
      setValue(`certificateFileTypes.${fileIndex}`, ids.join(","), { shouldDirty: true });
      revalidateCertificate();
    };

    const onAddDocumentType = (fileIndex: number) => {
      const prevTypes = getValues("certificateFileTypes") || [];
      const csv = prevTypes[fileIndex] || "";
      const ids = csv ? csv.split(",").filter(Boolean) : [];
      if (ids.length >= MAX_DOCUMENT_TYPES) return;

      const nextType = attachmentDocumentTypeOptions.find((opt) => !ids.includes(String(opt.value)));
      if (!nextType) return;

      // Prepend so the newly added tag is visible upfront (avoids being hidden behind +N overflow).
      ids.unshift(String(nextType.value));
      setValue(`certificateFileTypes.${fileIndex}`, ids.join(","), { shouldDirty: true });
      revalidateCertificate();
    };

    const handleFileRejections = (fileRejections: FileRejection[]) => {
      const listFileErrorSize: string[] = [];
      const listFileErrorType: string[] = [];

      fileRejections.forEach((fileReject) => {
        const file = fileReject.file;
        if (file.size > MAX_SIZE) {
          listFileErrorSize.push(file.name);
        }
        const isValid = rules.acceptMimeTypes?.some((t) => {
          const ext = file.name.split(".").pop()?.toLowerCase();
          return (t === file.type || rules.acceptFileTypesExtension?.includes(ext ?? "")) ?? false;
        });
        if (!isValid) {
          listFileErrorType.push(file.name);
        }
      });

      if (listFileErrorType.length > 0) {
        const extensions = getExtensionsOfMimeTypes(rules?.acceptFileTypesExtension || []);
        const errorType =
          `以下のファイル形式はアップロードできません。\n` +
          `${listFileErrorType.map((fileName) => `◦${fileName}`).join("\n")}\n` +
          `（アップロード可能なファイル形式は${extensions.join("、")}です。）`;
        toast.error(errorType);
        return;
      }

      if (listFileErrorSize.length > 0) {
        const errorSize = `以下のファイルはサイズ上限（${MAX_SIZE / 1000}KB）を超えているため、アップロードできませんでした：
          ${listFileErrorSize.map((fileName) => `・${fileName}`).join("\n")}
          `;
        toast.error(errorSize);
        return;
      }
    };

    const validateMaxTotalFiles = (newFilesCount: number, _currentFilesInFieldCount: number): string | undefined => {
      const remainingAllowed = getRemainingAllowedFiles();
      if (newFilesCount > remainingAllowed) {
        return `この人材のファイル件数が既に上限の10件に達しています。\n不要なファイルを削除してから、アップロードをお願いします。`;
      }
      return undefined;
    };

    const renderDocumentTypeTags = (index: number) => {
      const csv = (certificateFileTypes ?? [])[index] || "";
      const typeIds = csv ? String(csv).split(",").filter(Boolean) : [];
      return (
        <DocumentTypeTagsRow
          typeIds={typeIds}
          attachmentDocumentTypeOptions={attachmentDocumentTypeOptions}
          onTypeChange={(typeIndex, newValue) => handleTypeChange(index, typeIndex, newValue)}
          onAddType={() => onAddDocumentType(index)}
        />
      );
    };

    const renderCertificateFileFooter = (index: number) => {
      const file = (certificateFiles ?? [])[index];
      if (!file) return null;
      const csv = (certificateFileTypes ?? [])[index] || "";
      const hasType = String(csv).split(",").filter(Boolean).length > 0;
      if (hasType) return null;
      return (
        <Typography variant="cap12" color={text.error} mt={1}>
          書類の種類を選択してください。
        </Typography>
      );
    };

    // Ref to the certificateFiles area — scroll anchor for cert errors whose messages render
    // via footerCustom (per-file-type-required) or on a redirected path (required-types),
    // i.e. without a usable DOM ref attached to the RHF error.
    const certificateFilesRef = useRef<HTMLDivElement>(null);

    // Scroll to first error field (top-to-bottom DOM order) on every submit attempt.
    // Use submitCount (primitive, increments per attempt) instead of the errors object reference,
    // because RHF's formState.errors Proxy often keeps the same reference across submits — a
    // useEffect with `errors` in deps would not re-fire reliably.
    const submitCount = methods.formState.submitCount;
    useEffect(() => {
      if (!submitCount) return;
      setTimeout(() => {
        scrollToFirstError(methods.formState.errors, {
          customAnchors: {
            certificateFiles: certificateFilesRef.current,
            certificateFileTypes: certificateFilesRef.current,
          },
        });
      }, 100);
    }, [submitCount, methods]);

    // Read isDirty during render so react-hook-form's Proxy subscribes the component to formState.isDirty changes.
    // Accessing only inside useImperativeHandle (which runs at commit phase) does NOT establish the subscription,
    // so the component would not re-render when isDirty flips and the ref stays stale.
    const isDirty = methods.formState.isDirty;

    useImperativeHandle(
      ref,
      () => ({
        submit: () => {
          handleSubmit(onSubmit)();
        },
        isLoading: isSubmitting,
        isDirty,
      }),
      [isDirty, isSubmitting, handleSubmit, onSubmit],
    );

    return (
      <Form methods={methods} onSubmit={onSubmit} schema={caseMatchInfoSchema} key={`case-match-form-${status ?? "default"}`}>
        <InnerBox mt={3} p={3} showDivider={false} borderColor={black[95]} bordered={true}>
          <Stack>
            <Box mb={3}>
              <Typography variant="sub16Bold" color={black[30]}>
                マッチング情報
              </Typography>
            </Box>

            <Grid container rowSpacing={2} columnSpacing={3} sx={{ overflow: "hidden" }}>
              <Grid item xs={6}>
                <FormField control={control} name="casePersonInChargeId">
                  <Select placeholder="選択してください" options={caseUserOption} />
                </FormField>
              </Grid>

              <Grid item xs={6}>
                <FormField control={control} name="talentPersonInChargeId">
                  <Select placeholder="選択してください" options={talentUserOption} />
                </FormField>
              </Grid>

              <Grid item xs={6}>
                <FormField control={control} name="proposalDate" required={requiresProposalDate}>
                  <InputDatePicker />
                </FormField>
              </Grid>

              <Grid item xs={6}>
                <FormField control={control} name="interviewDate" required={requiresInterviewDate}>
                  <InputDatePicker />
                </FormField>
              </Grid>

              <Grid item xs={12}>
                <FormField control={control} name="japaneseLanguageProficiency" required={requiresJapaneseLanguageProficiency}>
                  <SelectMulti placeholder="選択してください" options={languageTestLevelOptions} maxDisplayLength={50} />
                </FormField>
              </Grid>

              <Grid item xs={12}>
                <FormField control={control} name="specificSkillCertificates" required={requiresSpecifiedSkillCertificateFile}>
                  <SelectMulti
                    placeholder="選択してください"
                    options={talent?.companySpecificIndustryId ? getCertificatesByIndustryId(talent.companySpecificIndustryId) : []}
                    separator="、"
                    maxDisplayLength={35}
                  />
                </FormField>
              </Grid>

              <Grid item xs={6}>
                <FormField control={control} name="majorId" required={requiresMajorId}>
                  <Select placeholder="選択してください" options={majorOptions} />
                </FormField>
              </Grid>

              <Grid item xs={6}>
                <FormField control={control} name="jobTypeId" required={requiresJobTypeId}>
                  <Select placeholder="選択してください" options={getJobTypeListOptionByMajor(Number(majorIdValue))} />
                </FormField>
              </Grid>

              <Grid item xs={12}>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                  <FormField control={control} name="tagIds" label="タグ">
                    <CheckboxGroup
                      nonControl
                      value={tagIdsValue ?? []}
                      flexDirection="row"
                      rowGap={1}
                      columnGap={3}
                      options={caseMatchTagOptions.map((opt) => ({ ...opt, value: String(opt.value) }))}
                      onChange={handleTagIdsChange}
                    />
                  </FormField>
                  <Button variant="ghost" size="sm" onClick={() => setIsTagEditorOpen(true)}>
                    編集
                  </Button>
                </Stack>
              </Grid>

              <Grid item xs={12} ref={certificateFilesRef}>
                <FormField control={control} name="certificateFiles" required={requiresCertificateFiles}>
                  <TalentDropZone
                    options={{ multiple: true, maxSize: MAX_SIZE }}
                    bordered={true}
                    rules={{
                      maxSize: MAX_SIZE,
                      acceptFileTypesExtension: rules.acceptFileTypesExtension,
                      acceptMimeTypes: rules.acceptMimeTypes,
                    }}
                    enableFileList={false}
                    enableFileListCustom={true}
                    twoColumnLayout={true}
                    onChange={onCertificateFilesChange}
                    handleFileRejections={handleFileRejections}
                    validateBeforeAdd={validateMaxTotalFiles}
                    nonControl
                    value={certificateFiles}
                    isEditMode={true}
                    childrenCustom={renderDocumentTypeTags}
                    footerCustom={renderCertificateFileFooter}
                  />
                </FormField>
                {/* Cross-file: each residence card side (表/裏) may be tagged on at most one file.
                    Non-blocking orange warning — does NOT prevent saving. */}
                {findDuplicateResidenceCardTypeIds(certificateFileTypes ?? []).length > 0 && (
                  <Typography variant="cap12" color={orange[80]} mt={1}>
                    {DUPLICATE_RESIDENCE_CARD_TYPE_MESSAGE}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12}>
                <FormField control={control} name="fitNote">
                  <Textarea placeholder="入力してください" minRows={4} />
                </FormField>
              </Grid>

              <Grid item xs={6}>
                <FormField control={control} name="companyOfferDate" required={requiresCompanyOfferDate}>
                  <InputDatePicker />
                </FormField>
              </Grid>

              <Grid item xs={6}>
                <FormField control={control} name="offerAcceptDate" required={requiresOfferAcceptDate}>
                  <InputDatePicker />
                </FormField>
              </Grid>

              <Grid item xs={12}>
                <FormField control={control} name="joinDateExpected" required={requiresJoinDateExpected}>
                  <InputDatePicker />
                </FormField>
              </Grid>

              <Grid item xs={6}>
                <FormField control={control} name="salesFee" required={requiresSalesFee}>
                  <Input placeholder="入力してください" endAdornment={<InputAdornment position="end">円</InputAdornment>} />
                </FormField>
              </Grid>

              <Grid item xs={6}>
                <FormField control={control} name="purchaseFee" required={requiresPurchaseFee}>
                  <Input placeholder="入力してください" endAdornment={<InputAdornment position="end">円</InputAdornment>} />
                </FormField>
              </Grid>

              <Grid item xs={12}>
                <FormField control={control} name="companyJoiningDate" required={requiresCompanyJoiningDate}>
                  <InputDatePicker />
                </FormField>
              </Grid>
            </Grid>
          </Stack>
        </InnerBox>
        <CaseMatchTagEditorModal isOpen={isTagEditorOpen} onClose={() => setIsTagEditorOpen(false)} />
      </Form>
    );
  },
);
